import { useState } from 'react';
import { useGAFormTracking } from '../../../../shared/hooks';
import { useWizard } from 'react-use-wizard';
import { ReferralFormData, useReferralContext } from '../../context';
import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { zipSchema } from '../../schemas/zipSchema';
import { zipProLookup } from '../../api';
import {
  getFormName,
  NUMERIC_INPUT_ATTRS,
  useTrackStepError,
  useTrackStepStarted
} from '../../utils';
import { trackStepCompleted } from '../../../../shared/analytics';
import {
  ProJoinOurProgram,
  StandardStepSubtitleText,
  StandardStepTitle,
  StepWrapper
} from '../../components';
import FormField from '@ramsey-design-system/form-field';
import Input from '@ramsey-design-system/input';
import Button from '@ramsey-design-system/button';
import { ArrowRightIcon } from '@ramsey-design-system/icons';

const STEP_NAME = 'location_step';
const STEP_LABEL =
  "We'll look for a tax pro near you (and if there are none, we'll connect you with our national partner).";

interface ComplexityLocationStepFields {
  postal_code: string;
}

function LocationStep() {
  const formRef = useGAFormTracking('complexity_where_are_you_located_step');
  const { nextStep, activeStep } = useWizard();
  const { referral, updateReferral, updateProInfo } = useReferralContext();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ComplexityLocationStepFields>({
    defaultValues: { postal_code: referral.postal_code },
    resolver: yupResolver(zipSchema)
  });
  const [isLoading, setIsLoading] = useState(false);

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);
  const trackError = useTrackStepError(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function fetchProLookup(postalCode: string) {
    try {
      const result = await zipProLookup(postalCode);
      updateProInfo(result);
    } catch {
      console.error('ProLookup Failed');
    }
  }

  async function onValidSubmit(referral: Partial<ReferralFormData>) {
    setIsLoading(true);
    updateReferral(referral);
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['Zip Code'],
      answer_values: [referral.postal_code!],
      form_name: getFormName()
    });

    await fetchProLookup(referral.postal_code ?? '');
    await nextStep();
    setIsLoading(false);
  }

  function onInvalid(formErrors: FieldErrors<ComplexityLocationStepFields>) {
    trackError('validation', formErrors.postal_code?.message, 'postal_code');
  }

  return (
    <StepWrapper>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onValidSubmit, onInvalid)}
        noValidate
      >
        <StandardStepTitle>Where are you located?</StandardStepTitle>
        <StandardStepSubtitleText>
          We'll find a tax pro serving your area.
        </StandardStepSubtitleText>

        <div className="mb-4">
          <FormField
            id="zip_code"
            label="Zip Code"
            size="lg"
            invalid={!!errors.postal_code}
            errors={[errors.postal_code?.message]}
          >
            {() => (
              <Input
                {...register('postal_code')}
                className={'!max-w-full'}
                size="lg"
                invalid={!!errors.postal_code}
                id="zip_code"
                data-testid="tax_zip_code_input"
                autoComplete="postal-code"
                {...NUMERIC_INPUT_ATTRS}
              />
            )}
          </FormField>
        </div>

        <Button
          data-testid="standard_next_button"
          type="submit"
          icon={isLoading ? undefined : ArrowRightIcon}
          iconPosition="right"
          className={'!w-full'}
          fullWidthOnMobile={true}
          size="large"
          loading={isLoading}
        >
          {isLoading ? 'Loading' : 'Next'}
        </Button>
      </form>
      <ProJoinOurProgram />
    </StepWrapper>
  );
}

export { LocationStep };
